/*
  Offer selector (v2 buy box).

  Responsibilities:
  - Keep the hidden `selling_plan` input in sync with the selected variant's
    selling plan (each quantity tier has its own plan/cadence).
  - Swap between the subscription and one-time products via the Section
    Rendering API when the segmented toggle is used (same mechanism as the
    old picker, driven by the one_time_product / subscription_product
    metafield data attributes on <product-info>).
  - Keep the CTA label in sync: "Start with 3 Pouches — £56.00" etc. All
    strings come from server-rendered data attributes — nothing is parsed
    from visible text.
  - Open/close the free-gift info dialog.
  - Dispatch `selling-plan-changed` so the <product-gifts> auto-add engine
    (gifts.liquid) keeps tracking subscription state.
*/

if (!customElements.get('offer-selector')) {
  customElements.define(
    'offer-selector',
    class OfferSelector extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;

        this.addEventListener('change', (event) => {
          if (event.target.matches('.offer-card__input')) {
            this.onVariantChange(event.target);
          }
        });

        this.addEventListener('click', (event) => {
          const giftOpen = event.target.closest('[data-offer-gift-open]');
          if (giftOpen) {
            // Inside a <label>: stop the click from also toggling the radio.
            event.preventDefault();
            event.stopPropagation();
            this.querySelector('[data-offer-gift-dialog]')?.showModal();
            return;
          }

          const giftClose = event.target.closest('[data-offer-gift-close]');
          if (giftClose) {
            event.preventDefault();
            this.querySelector('[data-offer-gift-dialog]')?.close();
            return;
          }

          const toggleBtn = event.target.closest('.offer-toggle__btn');
          if (toggleBtn && !toggleBtn.classList.contains('offer-toggle__btn--active')) {
            this.swapProduct(toggleBtn.dataset.offerMode);
          }
        });

        // Click on the dialog backdrop closes it.
        const dialog = this.querySelector('[data-offer-gift-dialog]');
        if (dialog) {
          dialog.addEventListener('click', (event) => {
            if (event.target === dialog) dialog.close();
          });
        }

        // Dawn re-renders parts of the buy box on variant change and may reset
        // the submit button label — re-apply ours after it settles.
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          const templateView = new URLSearchParams(window.location.search).get('view');
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            setTimeout(() => {
              this.updateCtaLabel();
              // Dawn's updateURL drops ?view= — restore it so template previews survive reloads.
              const params = new URLSearchParams(window.location.search);
              if (templateView && params.get('view') !== templateView) {
                params.set('view', templateView);
                window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
              }
            }, 50);
          });
        }

        this.reconcileSelection();
        // Browsers restore radio state on reload/back-nav without firing
        // change events, which would show one plan selected while the form
        // still buys the server default. Snap back to the default and resync.
        this.onPageshow = () => this.reconcileSelection();
        window.addEventListener('pageshow', this.onPageshow);
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        window.removeEventListener('pageshow', this.onPageshow);
      }

      reconcileSelection() {
        const serverDefault = this.querySelector('.offer-card__input[checked]');
        const current = this.checkedInput;
        if (serverDefault && current !== serverDefault) {
          serverDefault.checked = true;
          this.dispatchSellingPlanChanged(serverDefault);
        }
        this.syncSellingPlanInput();
        this.updateCtaLabel();
      }

      get checkedInput() {
        return this.querySelector('.offer-card__input:checked');
      }

      get sellingPlanInput() {
        return this.querySelector('.selling-plan-input');
      }

      onVariantChange(input) {
        this.syncSellingPlanInput();
        this.updateCtaLabel();
        this.dispatchSellingPlanChanged(input);
      }

      syncSellingPlanInput() {
        const input = this.checkedInput;
        const planInput = this.sellingPlanInput;
        if (!input || !planInput) return;
        planInput.value =
          this.dataset.productMode === 'subscription' ? input.dataset.sellingPlanId || '' : '';
      }

      updateCtaLabel() {
        const input = this.checkedInput;
        if (!input) return;
        const button = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!button || button.hasAttribute('disabled')) return;
        const span = button.querySelector('span');
        if (!span) return;
        // Subscription plans all use the "Start with" prefix; one-time uses "Add".
        const prefix =
          this.dataset.productMode === 'subscription'
            ? this.dataset.ctaHeroPrefix
            : this.dataset.ctaPrefix;
        span.textContent = `${prefix} ${input.dataset.variantTitle} — ${input.dataset.priceFormatted}`;
      }

      dispatchSellingPlanChanged(input) {
        document.dispatchEvent(
          new CustomEvent('selling-plan-changed', {
            detail: {
              price: parseInt(input.dataset.price, 10),
              comparePrice: parseInt(input.dataset.comparePrice, 10),
              sellingPlanId: this.sellingPlanInput?.value || '',
            },
          })
        );
      }

      swapProduct(targetMode) {
        const productInfo =
          this.closest('product-info') ||
          document.querySelector(`product-info[data-section="${this.sectionId}"]`);
        if (!productInfo) return;

        const productUrl =
          targetMode === 'subscription'
            ? productInfo.dataset.subscriptionProductUrl
            : productInfo.dataset.oneTimeProductUrl;
        if (!productUrl) return;

        const errorEl = this.querySelector('.offer-selector__swap-error');
        if (errorEl) errorEl.hidden = true;
        productInfo.classList.add('offer-selector--swapping');

        fetch(`${productUrl}?section_id=${this.sectionId}`, { headers: { Accept: 'text/html' } })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const newProductInfo = html.querySelector(
              `product-info[data-section="${this.sectionId}"]`
            );
            if (!newProductInfo) throw new Error('Product section not found in response');

            productInfo.classList.remove('offer-selector--swapping');
            OfferSelector.swapProductInfo(productInfo, newProductInfo);

            // Preserve template preview (?view=...) while updating the URL.
            const view = new URLSearchParams(window.location.search).get('view');
            if (productInfo.dataset.updateUrl === 'true') {
              window.history.replaceState({}, '', view ? `${productUrl}?view=${view}` : productUrl);
            }

            requestAnimationFrame(() => {
              setTimeout(() => {
                const current = document.querySelector(
                  `product-info[data-section="${this.sectionId}"]`
                );
                if (!current) return;
                const selector = current.querySelector('offer-selector');
                const checked = selector?.checkedInput;
                if (selector && checked) {
                  selector.syncSellingPlanInput();
                  selector.updateCtaLabel();
                  selector.dispatchSellingPlanChanged(checked);
                }
                current.dispatchEvent(
                  new CustomEvent('product-info:loaded', { bubbles: true })
                );
              }, 50);
            });
          })
          .catch((error) => {
            console.error('[offer-selector] product swap failed:', error);
            productInfo.classList.remove('offer-selector--swapping');
            const errEl = this.querySelector('.offer-selector__swap-error');
            if (errEl) errEl.hidden = false;
          });
      }

      static swapProductInfo(oldEl, newEl) {
        const dataAttrs = [
          'productId',
          'url',
          'oneTimeProductId',
          'oneTimeProductUrl',
          'subscriptionProductId',
          'subscriptionProductUrl',
        ];
        const applyDataAttrs = (target) => {
          dataAttrs.forEach((attr) => {
            if (newEl.dataset[attr]) {
              target.dataset[attr] = newEl.dataset[attr];
            } else {
              delete target.dataset[attr];
            }
          });
        };

        if (window.HTMLUpdateUtility) {
          window.HTMLUpdateUtility.viewTransition(oldEl, newEl, [], [applyDataAttrs]);
        } else {
          oldEl.innerHTML = newEl.innerHTML;
          applyDataAttrs(oldEl);
        }
      }
    }
  );
}
